import { useMemo, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import ConfirmationNumberOutlined from "@mui/icons-material/ConfirmationNumberOutlined";
import EventAvailableOutlined from "@mui/icons-material/EventAvailableOutlined";
import GroupsOutlined from "@mui/icons-material/GroupsOutlined";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import QrCode2Rounded from "@mui/icons-material/QrCode2Rounded";
import { useAuth } from "@bw/shared/hooks";
import {
  canonicalizeDropInPaymentOptions,
  formatDate,
  formatMoney,
} from "@bw/shared/utils";
import { DetailHeader } from "../../../common/components/DetailHeader";
import { BookingPassDialog } from "../../booking/components/BookingPassDialog";
import { useBookingPassStates } from "../../booking/hooks/useBookingPassStates";
import { useMyBookings } from "../../booking/hooks/useMyBookings";
import { CHECKIN_PASS_REFUSAL } from "../../../common/checkinPassRefusal";
import { BookedBy } from "../components/BookedBy";
import { CheckInCard, CheckInRow, VisitDots } from "../components/CheckInCard";
import { admissionUnavailableMessage } from "../components/PaymentMethodSelect";
import { useDropInPaymentOptions } from "../hooks/useDropInPaymentOptions";
import { useFacilityMode } from "../hooks/useFacilityMode";
import {
  formatFacilityTimeRange,
  isFacilityToday,
  visitDots,
} from "../lib/redemption";

const smallButtonSx = {
  height: 32,
  px: 1.5,
  fontSize: "0.8125rem",
  fontWeight: 600,
  borderRadius: "6px",
  whiteSpace: "nowrap",
  minWidth: 0,
} as const;


/**
 * Player app › Check in (approved frame, #2691). What the player can show at
 * the entrance: paid things show a code straight away (court bookings reuse the
 * booking pass), a pass or membership needs "Redeem one", and unpaid drop-in is
 * paid in the app first. A check-in code appears only for something paid or a
 * pass the player chose to redeem; the kiosk never takes payment.
 */
export function CheckInPage() {
  const { facilityId = "" } = useParams<{ facilityId: string }>();
  const { user } = useAuth();
  const optionsQ = useDropInPaymentOptions(facilityId);
  const facilityQ = useFacilityMode(facilityId);
  const bookingsQ = useMyBookings(facilityId);
  const passStatesQ = useBookingPassStates(facilityId);
  const [passBookingId, setPassBookingId] = useState<string | null>(null);
  const timeZone = facilityQ.data?.timezone ?? undefined;

  const options = optionsQ.data ? canonicalizeDropInPaymentOptions(optionsQ.data) : null;
  const facilityName = options?.facility_name ?? facilityQ.data?.name ?? "";

  // Owner rule (#2691): a booking that is checked in, over or cancelled shows
  // why instead of "Show code"; the server decides (the pass refuses too).
  const refusalById = useMemo(
    () => new Map((passStatesQ.data ?? []).map((state) => [state.booking_id, state.refusal ?? null])),
    [passStatesQ.data],
  );
  const todaysBookings = useMemo(() => {
    const now = Date.now();
    return (bookingsQ.data ?? [])
      .filter(
        (booking) =>
          ["confirmed", "completed", "cancelled"].includes(booking.status) &&
          isFacilityToday(booking.start_time, now, timeZone),
      )
      .sort((a, b) => a.start_time.localeCompare(b.start_time));
  }, [bookingsQ.data, timeZone]);
  const allScannable = todaysBookings.every((booking) => !refusalById.get(booking.booking_id));

  const packages = (options?.active_packages ?? []).filter(
    (option) => option.remaining_visits > 0,
  );
  const memberships = options?.active_memberships ?? [];
  const hasPasses = packages.length > 0 || memberships.length > 0;
  const checkedIn = options?.has_active_session === true;
  const openNow = options?.active_now === true;
  const canRedeem = openNow && !checkedIn;
  const singleTotal = options ? (options.single_total ?? options.single_price) : null;
  const showUnpaid =
    openNow &&
    !checkedIn &&
    options?.single_visit_available === true &&
    singleTotal !== null &&
    singleTotal !== undefined;

  const redeemButton = (subscriptionId: string, planName: string) =>
    canRedeem ? (
      <Button
        component={RouterLink}
        to={`/dropin/${facilityId}/check-in/${subscriptionId}`}
        variant="contained"
        disableElevation
        aria-label={`Redeem one from ${planName}`}
        sx={smallButtonSx}
      >
        Redeem one
      </Button>
    ) : (
      <Button variant="outlined" disabled sx={smallButtonSx}>
        {checkedIn ? CHECKIN_PASS_REFUSAL.checked_in_today.label : "Not open now"}
      </Button>
    );

  return (
    <Stack spacing={1.5} sx={{ width: "100%", maxWidth: 560, minHeight: "100%" }}>
      <DetailHeader
        title="Check in"
        back={{ to: `/dropin/${facilityId}`, label: "Back to drop-in" }}
        actions={
          facilityName ? (
            <Typography noWrap sx={{ fontSize: "0.78rem", color: "text.secondary", maxWidth: 180 }}>
              {facilityName}
            </Typography>
          ) : null
        }
      />
      <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary", lineHeight: 1.4 }}>
        Choose what you are here for. A check-in code appears only for something
        paid or a pass you redeem.
      </Typography>

      {optionsQ.isPending ? (
        <Stack direction="row" spacing={1} role="status" sx={{ alignItems: "center", py: 2 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Loading what you can check in with…
          </Typography>
        </Stack>
      ) : optionsQ.isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void optionsQ.refetch()}>
              Try again
            </Button>
          }
        >
          We couldn&apos;t load your passes for this facility.
        </Alert>
      ) : (
        <>
          {checkedIn ? (
            <Alert
              severity="success"
              action={
                <Button
                  component={RouterLink}
                  to={`/dropin/${facilityId}/session`}
                  color="inherit"
                  size="small"
                >
                  Open
                </Button>
              }
            >
              You&apos;re checked in for drop-in here.
            </Alert>
          ) : null}

          {todaysBookings.length > 0 ? (
            <CheckInCard
              title={allScannable ? "Paid, ready to scan" : "Your bookings today"}
              titleId="check-in-paid"
            >
              {todaysBookings.map((booking) => (
                <CheckInRow
                  key={booking.booking_id}
                  icon={<EventAvailableOutlined sx={{ color: "info.main" }} />}
                  title={`${booking.court_name ?? "Court"} · ${formatFacilityTimeRange(
                    booking.start_time,
                    booking.end_time,
                    timeZone,
                  )}`}
                  detail={
                    <BookedBy
                      booking={booking}
                      myUserId={user?.uid}
                      suffix={Number(booking.amount_charged ?? 0) > 0 ? "paid" : "confirmed"}
                    />
                  }
                  trailing={
                    refusalById.get(booking.booking_id) ? (
                      <Button variant="outlined" disabled sx={{ ...smallButtonSx, height: 28, px: 1 }}>
                        {CHECKIN_PASS_REFUSAL[refusalById.get(booking.booking_id)!].label}
                      </Button>
                    ) : (
                    <Button
                      variant="outlined"
                      color="inherit"
                      startIcon={<QrCode2Rounded />}
                      onClick={() => setPassBookingId(booking.booking_id)}
                      sx={{
                        ...smallButtonSx,
                        height: 28,
                        px: 1,
                        borderColor: "divider",
                        "& .MuiButton-startIcon": { mr: 0.5, "& svg": { fontSize: 16 } },
                      }}
                    >
                      Show code
                    </Button>
                    )
                  }
                />
              ))}
            </CheckInCard>
          ) : null}

          {hasPasses ? (
            <CheckInCard title="Your passes" titleId="check-in-passes">
              {packages.map((pass) => {
                const dots = visitDots(pass.total_visits, pass.remaining_visits);
                const useBy = pass.expires_at ? ` · use by ${formatDate(pass.expires_at)}` : "";
                return (
                  <CheckInRow
                    key={pass.subscription_id}
                    alignTop
                    icon={<ConfirmationNumberOutlined sx={{ color: "primary.main" }} />}
                    title={pass.plan_name}
                    detail={`${pass.remaining_visits} of ${pass.total_visits} left${useBy}`}
                    extra={
                      dots ? (
                        <VisitDots
                          dots={dots}
                          label={`${pass.remaining_visits} of ${pass.total_visits} visits left`}
                        />
                      ) : null
                    }
                    trailing={redeemButton(pass.subscription_id, pass.plan_name)}
                  />
                );
              })}
              {memberships.map((membership) => (
                <CheckInRow
                  key={membership.subscription_id}
                  icon={<ConfirmationNumberOutlined sx={{ color: "primary.main" }} />}
                  title={membership.plan_name}
                  detail="Unlimited drop-in · no visit is used"
                  trailing={redeemButton(membership.subscription_id, membership.plan_name)}
                />
              ))}
            </CheckInCard>
          ) : null}

          {hasPasses && !openNow && !checkedIn && options ? (
            <Typography variant="body2" color="text.secondary">
              {admissionUnavailableMessage(options)}
            </Typography>
          ) : null}

          {showUnpaid && options ? (
            <CheckInCard title="Today, not paid yet" titleId="check-in-unpaid">
              <CheckInRow
                icon={<GroupsOutlined sx={{ color: "text.secondary" }} />}
                title="Drop-in · open now"
                detail={`${
                  Number(singleTotal) > 0 ? formatMoney(singleTotal, options.currency) : "Free today"
                }${hasPasses ? ", or redeem a visit above" : ""}`}
                trailing={
                  <Button
                    component={RouterLink}
                    to={`/dropin/${facilityId}`}
                    variant="outlined"
                    color="inherit"
                    sx={{ ...smallButtonSx, height: 28, px: 1, borderColor: "divider" }}
                  >
                    {Number(singleTotal) > 0
                      ? `Pay ${formatMoney(singleTotal, options.currency)}`
                      : "Check in"}
                  </Button>
                }
              />
            </CheckInCard>
          ) : null}

          {!checkedIn && todaysBookings.length === 0 && !hasPasses && !showUnpaid ? (
            <Alert severity="info">
              Nothing to check in with here right now. Book a court or buy a pass
              first; the front desk can help too.
            </Alert>
          ) : null}
        </>
      )}

      <Box sx={{ flex: 1 }} />
      {hasPasses ? (
        <Stack
          direction="row"
          spacing={0.75}
          sx={{ alignItems: "flex-start", color: "text.secondary", fontSize: "0.75rem", lineHeight: 1.4 }}
        >
          <InfoOutlined aria-hidden sx={{ fontSize: 16 }} />
          <span>
            Redeem one uses a visit only when the kiosk scans the code. If the
            code expires unscanned, nothing is used.
          </span>
        </Stack>
      ) : null}

      {passBookingId ? (
        <BookingPassDialog bookingId={passBookingId} onClose={() => setPassBookingId(null)} />
      ) : null}
    </Stack>
  );
}
